// Comprime/redimensiona uma imagem no navegador ANTES do upload — pedido
// 2026-09-11 (foto do veículo, tela Início do PWA): "evitar upload
// desnecessariamente pesado; otimizar/comprimir a imagem se necessário" e
// "priorizar velocidade de upload". Uma foto direto da câmera de um
// celular atual costuma vir 3-8MB; em rede móvel isso é o gargalo real do
// fluxo (não o processamento do servidor, que já é rápido — ver
// StorageService#uploadImageWithVariants no gpsdrive-backend, que
// recodifica pra WebP e gera 4 miniaturas de qualquer forma). Reduzir o
// arquivo aqui antes de sair do celular é o que de fato acelera o upload
// percebido pelo usuário.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;
// Abaixo disso não vale o custo de recodificar (já é pequena o
// suficiente para qualquer rede) — evita gastar CPU/bateria à toa numa
// imagem que a câmera ou um app de terceiros já comprimiu bem.
const SKIP_BELOW_BYTES = 800 * 1024;

// Só JPEG/PNG/WebP passam por canvas — GIF perderia a animação, e formatos
// não suportados pelo navegador (ex.: HEIC em alguns Android/desktop)
// simplesmente não decodificam em createImageBitmap, capturado pelo
// try/catch abaixo. Em qualquer caso de dúvida ou falha, devolve o
// arquivo ORIGINAL intacto — esta função nunca é o motivo de um upload
// falhar; na pior hipótese, só deixa de otimizar.
const COMPRESSIBLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

// `maxDimension` maior pros fundos de tela cheia da Tela de Login (2560 —
// 1600 ficaria borrado num monitor grande). Nunca usar em logo: JPEG perde
// a transparência.
export async function compressImageFile(
  file: File,
  { maxDimension = MAX_DIMENSION }: { maxDimension?: number } = {},
): Promise<File> {
  if (!COMPRESSIBLE_TYPES.has(file.type) || file.size <= SKIP_BELOW_BYTES) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    // Só troca se realmente ficou menor — uma imagem já bem comprimida às
    // vezes recodifica maior; nesse caso o original continua sendo o
    // melhor upload possível.
    if (!blob || blob.size >= file.size) return file;

    const jpegName = file.name.replace(/\.[^./\\]+$/, "") + ".jpg";
    return new File([blob], jpegName, { type: "image/jpeg" });
  } catch {
    return file;
  }
}
